import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../../components';
import { Form, Input, Checkbox, Space, Divider, Alert, Row, Col, Skeleton, Card } from 'antd';
import { Select } from '../../../components';
import { CloudOutlined } from '@ant-design/icons';
import { useCreateSourceMutation, useUpdateSourceMutation, useLazyGetSourceByIdQuery } from '../../../api';
import TagsSelector from '../../../components/TagsSelector';
import MoveGroupSelector from '../../../components/MoveGroupSelector';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { handleError } from '../../../lib/helpers';
import { CLOUD_SIZE_CONFIG, NIL_UUID } from '../../../constants';
import { parseSourceInstructions } from '../utils';

const { Option } = Select;
const { TextArea } = Input;

const EditSourceModal = ({ open, onClose, onCancel, sourceId = null, userOrg }) => {
    const [form] = Form.useForm();
    const { notifySuccess, notifyError } = useNotificationContext();

    // State
    const [isEditMode, setIsEditMode] = useState(false);
    const [showCredentials, setShowCredentials] = useState(true);
    const [usePrivateKey, setUsePrivateKey] = useState(false);
    const [hasPreinstalledAgent, setHasPreinstalledAgent] = useState(true);
    const [storeCredentialsOnAppliance, setStoreCredentialsOnAppliance] = useState(false);
    const [addTargetForCloudFiles, setAddTargetForCloudFiles] = useState(false);
    const [targetHasPreinstalledAgent, setTargetHasPreinstalledAgent] = useState(false);
    const [useTargetPrivateKey, setUseTargetPrivateKey] = useState(false);
    const [cloudType, setCloudType] = useState('');
    const [cloudSizeOptions, setCloudSizeOptions] = useState([]);
    const [tags, setTags] = useState([]);
    const [moveGroupData, setMoveGroupData] = useState(null);

    const [createSource, { isLoading: isCreating }] = useCreateSourceMutation();
    const [updateSource, { isLoading: isUpdating }] = useUpdateSourceMutation();
    const [getSourceById, { data: sourceData, isLoading: isLoadingSource }] = useLazyGetSourceByIdQuery();

    const isLoading = isCreating || isUpdating || isLoadingSource;

    // Set edit mode based on sourceId
    useEffect(() => {
        setIsEditMode(!!sourceId);
        if (sourceId) {
            getSourceById(sourceId);
        }
    }, [sourceId, getSourceById]);

    const handleCloudTypeChange = useCallback(
        (selectedCloudType) => {
            setCloudType(selectedCloudType);
            if (selectedCloudType && CLOUD_SIZE_CONFIG[selectedCloudType]) {
                setCloudSizeOptions(CLOUD_SIZE_CONFIG[selectedCloudType]);
                // Clear cloud size when type changes
                form.setFieldValue('cloud_sizing', '');
            } else {
                setCloudSizeOptions([]);
                form.setFieldValue('cloud_sizing', '');
            }
        },
        [form],
    );

    // Populate form when editing
    useEffect(() => {
        if (isEditMode && sourceData) {
            // Parse existing cloud_sizing to extract type and size
            let parsedCloudType = '';
            let parsedCloudSize = '';
            if (sourceData.cloud_sizing) {
                // Try to parse format like "AWS:t2.micro" or just "t2.micro"
                const parts = sourceData.cloud_sizing.split(':');
                if (parts.length === 2) {
                    parsedCloudType = parts[0];
                    parsedCloudSize = parts[1];
                } else {
                    // If no type specified, try to detect from size
                    const size = sourceData.cloud_sizing;
                    for (const [type, sizes] of Object.entries(CLOUD_SIZE_CONFIG)) {
                        if (sizes.includes(size)) {
                            parsedCloudType = type;
                            parsedCloudSize = size;
                            break;
                        }
                    }
                }
            }

            const formData = {
                host: sourceData.host,
                move_group_id: sourceData.move_group_id,
                cloud_type: parsedCloudType,
                cloud_sizing: parsedCloudSize,
                control_connection_type: sourceData.control_connection_type,
                username: sourceData.credentials?.username || '',
                domain: sourceData.credentials?.domain || '',
                instructions: parseSourceInstructions(sourceData.instructions),
                target_host: sourceData.target_host || '',
                target_username: sourceData.target_credentials?.username || '',
                target_domain: sourceData.target_credentials?.domain || '',
                target_private_key: sourceData.target_credentials?.private_key || '',
                target_passphrase: sourceData.target_credentials?.passphrase || '',
            };

            form.setFieldsValue(formData);
            setTags(sourceData.tags || []);
            // Set move group data for edit mode
            if (sourceData?.move_group?.id && sourceData?.move_group?.id !== NIL_UUID) {
                setMoveGroupData({
                    mode: 'existing',
                    selectedMoveGroupId: sourceData.move_group.id,
                    newMoveGroupName: '',
                    targetMigrationDate: null,
                    assignedResourceId: '',
                });
            } else {
                setMoveGroupData(null);
            }
            setShowCredentials(false); // Hide credentials by default in edit mode
            setHasPreinstalledAgent(sourceData.control_connection_type === 'agent');
            setAddTargetForCloudFiles(!!sourceData.target_host); // Set based on whether target_host exists
            setTargetHasPreinstalledAgent(sourceData.target_control_connection_type === 'agent');
            setUseTargetPrivateKey(!!sourceData.target_credentials?.private_key);
            setStoreCredentialsOnAppliance(sourceData?.credentials?.storage === 'ca');

            // Set cloud type and populate sizes
            if (parsedCloudType) {
                setCloudType(parsedCloudType);
                setCloudSizeOptions(CLOUD_SIZE_CONFIG[parsedCloudType] || []);
            }
        } else if (!isEditMode) {
            // Reset form for add mode
            if (open) {
                form.resetFields();
                setTags([]);
                setMoveGroupData(null);
                setShowCredentials(true);
                setHasPreinstalledAgent(true);
                setAddTargetForCloudFiles(false);
                setTargetHasPreinstalledAgent(false);
                setUseTargetPrivateKey(false);
                setCloudType('');
                setCloudSizeOptions([]);
            }
        }
    }, [isEditMode, sourceData, form, open]);

    // Reset form when modal closes
    useEffect(() => {
        if (open) {
            form.resetFields();
            setTags([]);
            setMoveGroupData(null);
            setShowCredentials(true);
            setUsePrivateKey(false);
            setHasPreinstalledAgent(true);
            setStoreCredentialsOnAppliance(false);
            setAddTargetForCloudFiles(false);
            setTargetHasPreinstalledAgent(false);
            setUseTargetPrivateKey(false);
            setCloudType('');
            setCloudSizeOptions([]);
        }
    }, [open, form]);

    // Handle form submission
    const handleSubmit = useCallback(async () => {
        try {
            const values = await form.validateFields();

            // Parse instructions
            let instructions = {};
            if (values.instructions) {
                const instructionPairs = values.instructions.split(';');
                instructionPairs.forEach((pair) => {
                    const [key, value] = pair.split('=');
                    if (key && value) {
                        instructions[key.trim()] = value.includes(',')
                            ? value.split(',').map((v) => v.trim())
                            : value.trim();
                    }
                });
            }

            // Prepare source data following backbone createPayload logic
            const sourceData = {
                name: values.host?.trim() || '',
                host: values.host?.trim() || '',
                cloud_sizing:
                    values.cloud_type && values.cloud_sizing ? `${values.cloud_type}:${values.cloud_sizing}` : '',
                instructions,
                tags,
                organization_id: userOrg,
                control_connection_type: null,
            };

            // Handle credentials following backbone createPayload logic
            let credentials = {};
            if (hasPreinstalledAgent) {
                // Agent mode: empty credentials
                sourceData.control_connection_type = 'agent';
                credentials.storage = '';
                credentials.username = '';
                credentials.password = '';
                credentials.domain = '';
            } else if (storeCredentialsOnAppliance) {
                // CA storage mode
                credentials.storage = 'ca';
                credentials.username = '';
                credentials.password = '';
                credentials.domain = '';
            } else {
                // Local credentials mode
                credentials.storage = 'local';
                credentials.username = values.username || '';
                credentials.domain = values.domain?.trim() || null; // null if empty, not empty string

                if (usePrivateKey) {
                    credentials.private_key = values.private_key || '';
                    credentials.password = values.password || '';
                } else {
                    credentials.password = values.password || '';
                }
            }

            // Add credentials to payload (in edit mode only if editing credentials, always in create mode)
            if (isEditMode) {
                if (showCredentials) {
                    sourceData.credentials = credentials;
                }
                // Don't include credentials if not editing them in edit mode
            } else {
                // Always include credentials in create mode
                sourceData.credentials = credentials;
            }

            // Handle move group assignment following backbone logic
            if (moveGroupData) {
                if (moveGroupData.mode === 'existing' && moveGroupData.selectedMoveGroupId) {
                    sourceData.move_group_id = moveGroupData.selectedMoveGroupId;
                } else if (moveGroupData.mode === 'new' && moveGroupData.newMoveGroupName) {
                    // For new move groups, use NIL_UUID and provide move group details
                    sourceData.move_group_id = NIL_UUID;
                    sourceData.move_group_name = moveGroupData.newMoveGroupName.trim();
                    if (moveGroupData.targetMigrationDate) {
                        sourceData.target_migration_date = moveGroupData.targetMigrationDate.format('MM/DD/YYYY');
                    }
                    if (moveGroupData.assignedResourceId) {
                        sourceData.assigned_resource_email = moveGroupData.assignedResourceId; // Note: backbone uses 'email' not 'id'
                    }
                }
            } else {
                sourceData.move_group_id = NIL_UUID;
            }

            // Add target cloud files data if enabled (following backbone logic)
            if (addTargetForCloudFiles) {
                sourceData.target_host = values.target_host?.trim() || '';
                sourceData.target_control_connection_type = targetHasPreinstalledAgent ? 'agent' : null;

                if (!targetHasPreinstalledAgent) {
                    sourceData.target_credentials = {
                        username: values.target_username?.trim() || '',
                        domain: values.target_domain?.trim() || null, // null if empty
                    };

                    if (useTargetPrivateKey && values.target_private_key) {
                        sourceData.target_credentials.private_key = values.target_private_key || '';
                        sourceData.target_credentials.password = values.target_passphrase || '';
                    } else {
                        sourceData.target_credentials.password = values.target_password || '';
                    }
                }
            }

            // Submit form
            if (isEditMode) {
                await updateSource({ sourceId, sourceData }).unwrap();
                notifySuccess({ description: 'Source updated successfully' });
            } else {
                await createSource({ userOrg, sourceData }).unwrap();
                notifySuccess({ description: 'Source created successfully' });
            }

            onClose();
        } catch (error) {
            handleError(error, notifyError, `Failed to ${isEditMode ? 'update' : 'create'} source`);
        }
    }, [
        form,
        isEditMode,
        sourceId,
        userOrg,
        tags,
        hasPreinstalledAgent,
        showCredentials,
        storeCredentialsOnAppliance,
        usePrivateKey,
        createSource,
        updateSource,
        notifySuccess,
        notifyError,
        onClose,
        addTargetForCloudFiles,
        targetHasPreinstalledAgent,
        useTargetPrivateKey,
        moveGroupData,
    ]);

    // Check if we should show loading state
    const showLoadingState = isEditMode && isLoadingSource && !sourceData;

    return (
        <Modal
            title={isEditMode ? 'Update Source' : 'Add Source'}
            open={open}
            onOk={handleSubmit}
            onCancel={onCancel}
            confirmLoading={isLoading}
            width={800}
            maskClosable={false}
            destroyOnHidden={true}
        >
            {showLoadingState ? (
                <>
                    <Form form={form} component={false} />
                    <Skeleton active paragraph={{ rows: 8 }} />
                </>
            ) : (
                <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={isLoading}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Source IP Address"
                                name="host"
                                normalize={(value) => value?.trim()}
                                rules={[
                                    { required: true, message: 'Please enter source IP address' },
                                    { pattern: /^(\d{1,3}\.){3}\d{1,3}$/, message: 'Please enter a valid IP address' },
                                ]}
                            >
                                <Input placeholder="Enter source IP address" />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Tags */}
                    <Form.Item label="Add RiverMeadow Tags (optional)">
                        <TagsSelector
                            value={tags}
                            onChange={setTags}
                            placeholder="Enter tag name"
                            disabled={isLoading}
                        />
                    </Form.Item>

                    {/* Move Group */}
                    <Form.Item label="Application or Move Group Name (optional)">
                        <MoveGroupSelector
                            value={moveGroupData}
                            onChange={setMoveGroupData}
                            userOrg={userOrg}
                            disabled={isLoading}
                            hideCreateNew={false}
                        />
                    </Form.Item>

                    {/* Agent Configuration */}
                    <Form.Item>
                        <Checkbox
                            checked={hasPreinstalledAgent}
                            onChange={(e) => setHasPreinstalledAgent(e.target.checked)}
                        >
                            Source has preinstalled Agent
                        </Checkbox>
                    </Form.Item>

                    {!hasPreinstalledAgent && (
                        <>
                            <Form.Item>
                                <Checkbox
                                    checked={storeCredentialsOnAppliance}
                                    onChange={(e) => setStoreCredentialsOnAppliance(e.target.checked)}
                                >
                                    Store source credentials on the Migration Appliance
                                </Checkbox>
                            </Form.Item>

                            {isEditMode && (
                                <Form.Item>
                                    <Checkbox
                                        checked={showCredentials}
                                        onChange={(e) => setShowCredentials(e.target.checked)}
                                    >
                                        Edit Credentials
                                    </Checkbox>
                                </Form.Item>
                            )}

                            {/* Credentials Section */}
                            {(showCredentials || !isEditMode) && !storeCredentialsOnAppliance && (
                                <>
                                    <Divider>Credentials</Divider>

                                    <Form.Item
                                        label="Username"
                                        name="username"
                                        rules={[{ required: true, message: 'Please enter username' }]}
                                    >
                                        <Input placeholder="Enter username (required)" />
                                    </Form.Item>

                                    <Form.Item>
                                        <Checkbox
                                            checked={usePrivateKey}
                                            onChange={(e) => setUsePrivateKey(e.target.checked)}
                                        >
                                            Use SSH private key
                                        </Checkbox>
                                    </Form.Item>

                                    {usePrivateKey ? (
                                        <Form.Item
                                            label="Private Key"
                                            name="private_key"
                                            rules={[{ required: true, message: 'Please enter private key' }]}
                                        >
                                            <TextArea rows={4} placeholder="Enter private key (required)" />
                                        </Form.Item>
                                    ) : (
                                        <>
                                            <Form.Item
                                                label="Password"
                                                name="password"
                                                rules={[{ required: true, message: 'Please enter password' }]}
                                            >
                                                <Input.Password placeholder="Enter password (required)" />
                                            </Form.Item>

                                            <Form.Item
                                                label="Confirm Password"
                                                name="confirm_password"
                                                dependencies={['password']}
                                                rules={[
                                                    { required: true, message: 'Please confirm password' },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (!value || getFieldValue('password') === value) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error('Passwords do not match'));
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Input.Password placeholder="Confirm password (required)" />
                                            </Form.Item>
                                        </>
                                    )}

                                    <Form.Item label="Domain" name="domain">
                                        <Input placeholder="Enter Domain Name (optional)" />
                                    </Form.Item>

                                    <Alert
                                        title="Source credentials can be stored on the Migration Appliance."
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                </>
                            )}
                        </>
                    )}

                    {/* Target for Cloud Files */}
                    <Form.Item>
                        <Checkbox
                            checked={addTargetForCloudFiles}
                            onChange={(e) => setAddTargetForCloudFiles(e.target.checked)}
                        >
                            Add Target for Cloud Files
                        </Checkbox>
                    </Form.Item>

                    {addTargetForCloudFiles && (
                        <Card
                            title={
                                <Space>
                                    <CloudOutlined />
                                    <span>Target for Cloud Files</span>
                                </Space>
                            }
                            style={{
                                marginTop: 16,
                                marginBottom: 16,
                            }}
                        >
                            <Form.Item
                                label="Target IP"
                                name="target_host"
                                normalize={(value) => value?.trim()}
                                rules={[
                                    { required: addTargetForCloudFiles, message: 'Please enter target IP address' },
                                ]}
                            >
                                <Input placeholder="Enter Target IP address (required)" />
                            </Form.Item>

                            <Form.Item>
                                <Checkbox
                                    checked={targetHasPreinstalledAgent}
                                    onChange={(e) => setTargetHasPreinstalledAgent(e.target.checked)}
                                >
                                    Target has preinstalled Agent
                                </Checkbox>
                            </Form.Item>

                            {!targetHasPreinstalledAgent && (
                                <Card
                                    type="inner"
                                    title="Target Credentials"
                                    style={{
                                        marginTop: 16,
                                    }}
                                >
                                    <Form.Item
                                        label="Target Username"
                                        name="target_username"
                                        rules={[
                                            { required: !targetHasPreinstalledAgent, message: 'Please enter username' },
                                        ]}
                                    >
                                        <Input placeholder="Enter username (required)" />
                                    </Form.Item>

                                    <Form.Item>
                                        <Checkbox
                                            checked={useTargetPrivateKey}
                                            onChange={(e) => setUseTargetPrivateKey(e.target.checked)}
                                        >
                                            Use SSH private key
                                        </Checkbox>
                                    </Form.Item>

                                    {useTargetPrivateKey ? (
                                        <>
                                            <Form.Item
                                                label="Target Private Key"
                                                name="target_private_key"
                                                rules={[
                                                    {
                                                        required: useTargetPrivateKey,
                                                        message: 'Please enter private key',
                                                    },
                                                ]}
                                            >
                                                <TextArea rows={4} placeholder="Enter private key (required)" />
                                            </Form.Item>

                                            <Form.Item label="Target Passphrase" name="target_passphrase">
                                                <Input.Password placeholder="Enter passphrase (optional)" />
                                            </Form.Item>

                                            <Form.Item
                                                label="Confirm Target Passphrase"
                                                name="confirm_target_passphrase"
                                                dependencies={['target_passphrase']}
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (
                                                                !value ||
                                                                getFieldValue('target_passphrase') === value
                                                            ) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error('Target passphrases do not match'),
                                                            );
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Input.Password placeholder="Confirm passphrase (optional)" />
                                            </Form.Item>
                                        </>
                                    ) : (
                                        <>
                                            <Form.Item
                                                label="Target Password"
                                                name="target_password"
                                                rules={[
                                                    {
                                                        required: !useTargetPrivateKey,
                                                        message: 'Please enter target password',
                                                    },
                                                ]}
                                            >
                                                <Input.Password placeholder="Enter Target password (required)" />
                                            </Form.Item>

                                            <Form.Item
                                                label="Confirm Target Password"
                                                name="confirm_target_password"
                                                dependencies={['target_password']}
                                                rules={[
                                                    {
                                                        required: !useTargetPrivateKey,
                                                        message: 'Please confirm target password',
                                                    },
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            if (!value || getFieldValue('target_password') === value) {
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(
                                                                new Error('Target passwords do not match'),
                                                            );
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Input.Password placeholder="Confirm Target password (required)" />
                                            </Form.Item>
                                        </>
                                    )}

                                    <Form.Item label="Target Domain" name="target_domain">
                                        <Input placeholder="Enter target domain (optional)" />
                                    </Form.Item>
                                </Card>
                            )}
                        </Card>
                    )}

                    {/* Advanced Instructions */}
                    <Form.Item label="Advanced Instructions (optional)" name="instructions">
                        <TextArea rows={3} placeholder="Examples (name1=value1;name2=value2,value3...)" />
                    </Form.Item>

                    {/* Cloud Size Settings */}
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Cloud Type" name="cloud_type">
                                <Select placeholder="Select a Cloud Type" onChange={handleCloudTypeChange} allowClear>
                                    {Object.keys(CLOUD_SIZE_CONFIG).map((type) => (
                                        <Option key={type} value={type}>
                                            {type}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Cloud Size" name="cloud_sizing">
                                <Select
                                    placeholder="Select a Cloud Size"
                                    disabled={!cloudType || cloudSizeOptions.length === 0}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.children?.toLowerCase().includes(input.toLowerCase())
                                    }
                                >
                                    {cloudSizeOptions.map((size) => (
                                        <Option key={size} value={size}>
                                            {size}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )}
        </Modal>
    );
};

export default EditSourceModal;
